import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  borrowingBullets,
  borrowingHeadline,
  formatAud,
  headroomBullet,
  stressHeadline,
  stressSourceBullet,
  type BorrowingPowerOutput,
  type RepaymentStressOutput,
} from "@aucash/rules/borrowing-power";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented, Stepper } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// borrowing-power — the indicative borrowing-power + repayment-stress surface,
// stateless-v1 (PLAN arch decision #1). One screen, two modes over the shared
// APRA ruleset; the result renders INLINE (no save-by-id, no auth gate, no
// persistence). Each compute is a stateless `calc.borrowingPower.compute` query.
//
// THIS IS THE MOST REGULATORY-SENSITIVE SURFACE IN THE SUITE. The framing is
// binding: the result is an INDICATIVE ESTIMATE that makes the INVISIBLE LEVERS
// legible — the APRA 3.0pp serviceability buffer (why a bank's number is lower
// than "income × 5"), the HEM living-expense FLOOR (a licensed, non-public
// benchmark we only APPROXIMATE — flagged an estimate, user-adjustable), and the
// APRA DTI≥6 portfolio rationing (surfaced as LENDER rationing, NEVER "you can't
// borrow >6×"). It is NOT a pre-approval, NOT "you can borrow $X so apply".
//
// NCCP credit-assistance kill-line (BINDING): a home loan is NCCP credit, so a
// personalised result + ANY broker/lender/loan-product handoff = "arranging"
// without an ACL. There is NO lender/broker/loan CTA anywhere on this surface.
// All copy comes verbatim from the rules layer's helpers (which run the
// `assertNoCreditHandoff` guard), so wording can never drift to a handoff and
// the API re-asserts the same guard over the whole response.

const ASSESSMENT_DATE = "2026-02-01";

type Mode = "borrowing-power" | "repayment-stress";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "borrowing-power", label: "Borrowing estimate" },
  { value: "repayment-stress", label: "Rate-rise headroom" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  "borrowing-power": {
    eyebrow: "APRA settings · from 1 Feb 2026",
    title: "What might a lender lend — and why so much less than “income × 5”?",
    blurb:
      "An indicative estimate that makes the invisible levers legible: the APRA serviceability buffer lenders assess you against, the living-expense floor they apply, and where the result sits against the high-debt rationing band. An estimate lenders assess differently — not a pre-approval.",
  },
  "repayment-stress": {
    eyebrow: "APRA buffer · personal headroom",
    title: "What were you stress-tested to handle?",
    blurb:
      "When you were approved, the lender checked you could still meet repayments at your rate plus the APRA serviceability buffer. See that buffered repayment, the extra per month it represents, and a sensitivity strip — the headroom you were tested to, not a prediction of where rates go.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.borrowingPower.compute.query>
>;

export default function BorrowingPowerIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("borrowing-power");

  // borrowing-power fields.
  const [netAnnualIncome, setNetAnnualIncome] = useState("");
  const [declaredAnnualExpenses, setDeclaredAnnualExpenses] = useState("");
  const [existingAnnualDebtCommitments, setExistingAnnualDebtCommitments] =
    useState("");
  const [hemFloorAnnual, setHemFloorAnnual] = useState("");
  const [couple, setCouple] = useState(false);
  const [dependants, setDependants] = useState(0);

  // repayment-stress fields.
  const [loanAmount, setLoanAmount] = useState("");
  const [offsetBalance, setOffsetBalance] = useState("");

  // shared.
  const [rate, setRate] = useState(""); // entered as a percent string, e.g. "6.1"
  const [loanTermYears, setLoanTermYears] = useState(30);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");
  /** Percent string → fraction (e.g. "6.1" → 0.061), clamped to [0,1). */
  const rateFraction = (s: string): number => {
    const pct = Number(s || "0");
    return Number.isFinite(pct) ? Math.max(0, Math.min(99.99, pct)) / 100 : 0;
  };

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.borrowingPower.compute.query>[0]["input"];
  } | null => {
    if (mode === "repayment-stress") {
      return {
        input: {
          mode: "repayment-stress",
          loanAmount: num(loanAmount),
          currentRate: rateFraction(rate),
          loanTermYears,
          repaymentType: "principal-and-interest",
          offsetBalance: num(offsetBalance),
          assessmentDate: ASSESSMENT_DATE,
        },
      };
    }
    return {
      input: {
        mode: "borrowing-power",
        netAnnualIncome: num(netAnnualIncome),
        declaredAnnualExpenses: num(declaredAnnualExpenses),
        existingAnnualDebtCommitments: num(existingAnnualDebtCommitments),
        // Only send a HEM override when the user typed one; otherwise the rules
        // layer uses its documented (estimate) approximation.
        ...(hemFloorAnnual.trim() !== ""
          ? { hemFloorAnnual: num(hemFloorAnnual) }
          : {}),
        couple,
        dependants,
        productRate: rateFraction(rate),
        loanTermYears,
        repaymentType: "principal-and-interest",
        assessmentDate: ASSESSMENT_DATE,
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (rate.trim() === "")
      next.rate = "Enter your interest rate, e.g. 6.1.";
    if (mode === "repayment-stress") {
      if (loanAmount.trim() === "")
        next.loan = "Enter your outstanding loan amount.";
      return next;
    }
    if (netAnnualIncome.trim() === "")
      next.income = "Enter your net (after-tax) annual income.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.borrowingPower.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Borrowing power" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "repayment-stress" ? (
            <>
              <Field
                label="Your outstanding loan amount"
                help="The current balance owing on the loan you want to stress-test."
                error={errors.loan}
              >
                <MoneyInput
                  value={loanAmount}
                  onChange={setLoanAmount}
                  testID="input-loan-amount"
                  accessibilityLabel="Your outstanding loan amount"
                />
              </Field>
              <Field
                label="Offset / redraw balance (if any)"
                help="Reduces the interest-bearing balance — interest is charged on the loan minus this amount."
              >
                <MoneyInput
                  value={offsetBalance}
                  onChange={setOffsetBalance}
                  accessibilityLabel="Offset or redraw balance"
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Your net (after-tax) annual income"
                help="Income available to service a loan, after tax — for a couple applying together, your combined net income."
                error={errors.income}
              >
                <MoneyInput
                  value={netAnnualIncome}
                  onChange={setNetAnnualIncome}
                  testID="input-net-income"
                  accessibilityLabel="Your net after-tax annual income"
                />
              </Field>
              <Field
                label="Your declared annual living expenses"
                help="What you actually spend in a year. Lenders apply the higher of this and an indicative living-expense floor (HEM) — so declaring less than the floor does not lift the estimate."
              >
                <MoneyInput
                  value={declaredAnnualExpenses}
                  onChange={setDeclaredAnnualExpenses}
                  accessibilityLabel="Your declared annual living expenses"
                />
              </Field>
              <Field
                label="Existing annual debt commitments"
                help="Annual repayments on other loans, plus the notional repayment lenders count on your credit-card limits. An adjustable assumption, not a precise figure."
              >
                <MoneyInput
                  value={existingAnnualDebtCommitments}
                  onChange={setExistingAnnualDebtCommitments}
                  accessibilityLabel="Existing annual debt commitments"
                />
              </Field>
              <Field label="Are you a couple applying together?">
                <Checkbox
                  checked={couple}
                  onChange={setCouple}
                  label="Yes — a couple. This raises the indicative living-expense floor used."
                />
              </Field>
              <Field
                label="How many dependants?"
                help="Feeds the indicative living-expense floor (HEM) when you don't set your own below."
              >
                <Stepper
                  value={dependants}
                  onChange={setDependants}
                  min={0}
                  max={6}
                  testID="dependants"
                  accessibilityLabel="Number of dependants"
                />
              </Field>
              <Field
                label="Adjust the living-expense floor (HEM) — optional"
                help="HEM is an indicative estimate based on published methodology, not a precise figure (the real tables are licensed and not public). Leave blank to use our approximation, or set your own annual figure."
              >
                <MoneyInput
                  value={hemFloorAnnual}
                  onChange={setHemFloorAnnual}
                  accessibilityLabel="Adjust the living expense floor, annual, optional"
                />
              </Field>
            </>
          )}

          <Field
            label={
              mode === "repayment-stress"
                ? "Your current interest rate (%)"
                : "The interest rate you'd pay (%)"
            }
            help="As a percentage, e.g. 6.1. Lenders assess your repayments at this rate plus the APRA serviceability buffer."
            error={errors.rate}
          >
            <View style={styles.rateWrap}>
              <TextInput
                value={rate}
                onChangeText={(t) => setRate(t.replace(/[^0-9.]/g, ""))}
                keyboardType="decimal-pad"
                placeholder="6.1"
                placeholderTextColor={colors.muted}
                testID="input-rate"
                accessibilityLabel="Interest rate, as a percentage"
                style={styles.rateInput}
              />
              <Text style={styles.ratePct}>%</Text>
            </View>
          </Field>

          <Field
            label="Loan term (years)"
            help="The term used for the notional repayment — typically 25 or 30 years."
          >
            <Stepper
              value={loanTermYears}
              onChange={setLoanTermYears}
              min={1}
              max={40}
              suffix="yrs"
              testID="loan-term"
              accessibilityLabel="Loan term in years"
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="borrowing-power-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer sits at the TOP, before any number (validation §3.2
 * placement rule). Every figure + bullet comes from the rules-layer copy
 * helpers, which run the no-credit-handoff guard — there is NO lender/broker/
 * loan CTA anywhere on this personalised surface. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullets } = renderFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · APRA settings from {result.settingsDate}</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        <Text style={styles.notPreApproval}>
          An estimate lenders will assess differently — not a pre-approval.
        </Text>
      </Card>

      <Section title="What's driving this estimate">
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Section>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          Figures use the APRA serviceability buffer and high-DTI settings in
          force from {result.settingsDate}, last verified{" "}
          {result.sourcesVerifiedAt}.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helpers for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing, no handoff. */
function renderFor(result: ComputeResult): {
  headline: string;
  bullets: string[];
} {
  if (result.mode === "borrowing-power") {
    const out = result.output as BorrowingPowerOutput;
    return {
      headline: borrowingHeadline(out),
      bullets: borrowingBullets(
        out,
        result.settingsDate,
        result.sourcesVerifiedAt,
      ),
    };
  }
  const out = result.output as RepaymentStressOutput;
  return {
    headline: stressHeadline(out),
    bullets: [
      headroomBullet(out),
      stressSourceBullet(result.settingsDate, result.sourcesVerifiedAt),
    ],
  };
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline + bullet strings already embed the formatted figures, so it isn't
// called directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  rateWrap: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    backgroundColor: colors.card,
    paddingHorizontal: space.lg,
  },
  rateInput: {
    flex: 1,
    fontFamily: fonts.bodySemibold,
    fontSize: 18,
    color: colors.ink,
    paddingVertical: space.md,
  },
  ratePct: {
    fontFamily: fonts.bodySemibold,
    fontSize: 18,
    color: colors.muted,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  notPreApproval: {
    fontFamily: fonts.bodyMedium,
    fontSize: 13,
    color: colors.muted,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
